import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CellClickEvent, SelectableMode, SelectableSettings } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { ActionStageEnum } from 'src/app/modules/raci/models/enums/action-stage-enum';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { GroupApprovalData } from '../../../raci/models/group-approval-data';
import { RaciService } from '../../../raci/services/raci.service';
import { ValoremPublishStatusEnum } from '../../models/valorem-publish-status-enum';
import { AppStorageService } from '../../../../services/app-storage.service';

/**
 * Handles the valorem approval matrix component
 */
@Component({
  selector: 'valorem-approval-matrix',
  templateUrl: './valorem-approval-matrix.component.html',
  styleUrls: ['./valorem-approval-matrix.component.css']
})
export class ValoremApprovalMatrixComponent implements OnInit, OnDestroy {
  /**
 * Indicates whether approving is allowed
 */
  public _allowApproving = true;

  /**
   * Approval dialog is displayed?
   */
  public isApprovalDialogDisplayed = false;

  /**
   * Sets the value for allowing approving
   * @param value The value to set
   */
  @Input() set allowApproving(value: any) {
    this._allowApproving = value;
  }

  /**
   * Sets the current task
   * @param data The current task data
   */
  @Input() set currentTask(data: any) {
    if (data) {
      this.activeTask = data;
    }
  }

  /**
   * The active task
   */
  activeTask: any;

  /**
   * Indicates whether to show title info
   */
  @Input() showTitleInfo = false;

  /**
   * The grouping mode
   */
  @Input() groupBy: string;

  /**
   * The active portfolio ID
   */
  @Input() activePortfolioId: any;

  /**
   * The active project ID
   */
  @Input() activeProjectId: any;

  /**
   * Sets the data for the component
   * @param value The data value to set
   */
  @Input() set data(value: any) {
    if (value) {
      this.resetSelections();
      this.properties = value;
      this.filterPropertiesByDataStatus();
    }
  }

  /**
   * Sets the publish information
   * @param value The publish information value to set
   */
  @Input() set publishInformation(value: any) {
    if (value) {
      this.valoremPublishInformation = value;
    }
  }

  /**
   * Event emitter for updating the active task
   */
  @Output() updateActiveTask: EventEmitter<any> = new EventEmitter<any>();

  /**
   * The properties array
   */
  properties: any[];

  /**
   * The selected group
   */
  selectedGroup: string;

  /**
   * Array of properties grouped by sector
   */
  propertiesBySector: Array<GroupApprovalData> = [];

  /**
   * Array of properties grouped by region
   */
  propertiesByRegion: Array<GroupApprovalData> = [];

  /**
   * The group data
   */
  groupData: any;

  /**
   * The project
   */
  project: any;

  /**
   * The valuation reports
   */
  valuationReports: any;

  /**
   * The selected report ID
   */
  selectedReportId: any;

  /**
   * Array of selected approvals
   */
  selectedApprovals: Array<any> = [];

  /**
   * Indicates whether a project is selected
   */
  isProjectSelected = false;

  /**
   * Indicates whether the publish to Valorem icon is displayed
   */
  isPublishToValoremIconDisplayed = false;

  /**
   * The Valorem publish information
   */
  valoremPublishInformation: any;

  /**
   * The selectable mode
   */
  mode: SelectableMode = "single";

  /**
   * Indicates whether dragging is enabled
   */
  drag = false;

  /**
   * The selectable settings
   */
  selectableSettings: SelectableSettings;

  /**
   * Enum for Valorem publish status
   */
  valoremPublishStatusEnum: typeof ValoremPublishStatusEnum = ValoremPublishStatusEnum;

  /**
   * The project ID
   */
  projectId: number;

  /**
   * Array of subscriptions
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
   * Constructor
   * @param _raciNavigationService RaciNavigationService
   * @param _raciService RaciService
   */
  constructor(private _raciNavigationService: RaciNavigationService,
    private appStorage: AppStorageService,
    private _raciService: RaciService) {
    this.setSelectableSettings();
  }

  /**
   * OnInit
   * */
  ngOnInit(): void {
    const portfolio = this.appStorage.getSelectedPortfolio();
    this.projectId = portfolio?.ProjectID;
    this.subscribeToSaveApprovalEvent();
    this.activePortfolioId = this.activeTask?.taskValuationPortfolioId ?? this.activeTask.portfolioId;
    this.activeProjectId = this.projectId ?? this.activeTask.projectId;
    this.getReports();
    this._subscriptions.push(this._raciNavigationService._openReport.subscribe(data => {
      if (data) {
        this.createReportLink();
      }
    }));
  }

  /**
   * On destroy
   * */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._raciNavigationService.onSyncWithValorem(false);
  }

  /**
 * On changes actions
 * */
  ngOnChanges() {
    if (this.properties) {
      this.groupProperties();
    }
  }

  /**
   * Retrieves action stage enum values
   * */
  get ActionStageEnum() {
    return ActionStageEnum;
  }

  /**
   * Filter participants by data status
   */
  filterPropertiesByDataStatus() {
    this.properties = this.properties.filter(property => {
      if (this.properties.filter(item => item.propertyId == property.propertyId).length > 1) {
        return this.properties.find(item => item.propertyId == property.propertyId && property.dataStatus == "SUCCESS") ??
          this.properties.find(item => item.propertyId == property.propertyId && property.dataStatus == "ERROR");
      } else {
        return property;
      }
    });
  }

  /**
   * Create report link
   * */
  createReportLink() {
    if (this.selectedReportId) {
      const absUrl = window.location.href;//http://localhost/polluxApp/polluxValuation/app/pollux.html#/pd
      let url = absUrl.split("#")[0];//http://localhost/polluxApp/polluxValuation/app/pollux.html

      url += "#/rd";
      url += "?ViewReportId=" + this.selectedReportId;//http://localhost/polluxApp/polluxValuation/app/pollux.html#/rd?ViewReportId=1216
      window.open(url, '_blank');
    }
  }

  /**
   * Get Reports of valuation event
   * */
  getReports() {
    this._subscriptions.push(this._raciService.getReports(this.activeProjectId, this.activePortfolioId).subscribe(data => {
      if (data) {
        this.valuationReports = data;
      }
    }));
  }

  /**
   * Set Selectable Settings
   */
  public setSelectableSettings(): void {
    this.selectableSettings = {
      mode: this.mode,
      drag: this.drag,
    };
  }

  /**
   * Groups properties by region
   * */
  private groupByRegion() {
    this.propertiesByRegion = [];
    this.properties.forEach(property => {
      if (!this.propertiesByRegion.find(item => { return item.group == property.region })) {
        this.propertiesByRegion.push(new GroupApprovalData({ "group": property.region }));
      }
      this.propertiesByRegion.find(item => { return item.group == property.region }).tasks.push(property);
    });

    this.propertiesByRegion.forEach(region => {
      this.setMostCommonApprover(region);
    });

    this.checkForUnknownGroup(this.propertiesByRegion);
  }

  /**
   * Checks for the most commpon approver
   * @param approvalList Approvals list
   * @param approvalIndex Approval index (eg. 1,2,3)
   * @param approvalA First approval for comparison
   * @param approvalB Second approval for comparison
   */
  private getMostCommonApprover(approvalList: any[], approvalIndex: number, approvalA: any, approvalB: any) {
    return approvalList.filter(v => v[`approval${approvalIndex}`] === approvalB[`approval${approvalIndex}`]).length - approvalList.filter(v => v[`approval${approvalIndex}`] === approvalA[`approval${approvalIndex}`]).length;
  }

  /**
   * Sets most common approvers for specified group
   * @param group Group object
   */
  private setMostCommonApprover(group: any) {
    for (let approvalIndex = 1; approvalIndex <= 3; approvalIndex++) {
      const mostCommonApprover = group.tasks.sort((approvalA, approvalB) => this.getMostCommonApprover(group.tasks, approvalIndex, approvalA, approvalB))[0];
      const pendingApproval = group.tasks.find(x => !x[`dateApproved${approvalIndex}`] && !x[`dateRejected${approvalIndex}`]);
      group.approvals[0][`approval${approvalIndex}`] = mostCommonApprover[`approval${approvalIndex}`];
      group.approvals[0][`approvalUser${approvalIndex}`] = mostCommonApprover[`approvalUser${approvalIndex}`];
      group.approvals[0][`dateRejected${approvalIndex}`] = !mostCommonApprover[`dateRejected${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateRejected${approvalIndex}`]);
      group.approvals[0][`dateApproved${approvalIndex}`] = !mostCommonApprover[`dateApproved${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateApproved${approvalIndex}`]);
    }
  }

  /**
   * Check if the group list contains a gorup without name and sets it name to "Unknown"
   * @param groupList Group list
   */
  private checkForUnknownGroup(groupList: any[]) {
    const unknownSector = groupList.find(item => { return !item.group });
    if (unknownSector) {
      unknownSector.group = "Unknown";
    }
  }

  /**
   * Groups properties by sector
   * */
  private groupBySector() {
    this.propertiesBySector = [];
    this.properties.forEach(property => {
      if (!this.propertiesBySector.find(item => { return item.group == property.sector })) {
        this.propertiesBySector.push(new GroupApprovalData({ "group": property.sector }));
      }
      this.propertiesBySector.find(item => { return item.group == property.sector }).tasks.push(property);
    });

    this.propertiesBySector.forEach(sector => {
      this.setMostCommonApprover(sector);
    });

    this.checkForUnknownGroup(this.propertiesBySector);
  }

  /**
   *  Retieves all grid rows
   * */
  public getAllRows(): Array<HTMLTableElement> {
    return Array.from(document.querySelectorAll(".properties tr"));
  }

  /**
   * Selection change event for grouped data
   * @param selectedGroup Selected group
   */
  onSelectionChange(selectedGroup: string) {
    this.groupData.forEach(group => {
      if (group.group != selectedGroup) {
        group.selectedRows = [];
      }
    });
  }

  /**
   * On approval selection event
   * @param cell Selected kendo grid cell
   */
  public onRowSelection(cell: CellClickEvent) {
    const report = this.valuationReports?.find(report => { return report.PropertyId == cell.dataItem.propertyId })
    if (report) {
      this._raciNavigationService.onSelectRow(true);
      this.selectedReportId = report.Oid;
    }
  }

  /**
   * Group proerties by sector or region.
   * */
  private groupProperties() {
    this.groupBySector();
    this.groupByRegion();
    this.groupData = this.groupBy == 'Sector' ? this.propertiesBySector : this.propertiesByRegion;
    this._raciNavigationService.onSelectRow(false);
  }

  /**
   * Resets grid selections
   * */
  private resetSelections() {
    this.selectedApprovals = [];
    this.isProjectSelected = false;
  }

  /**
   * Subscribes to save approval event
   * */
  private subscribeToSaveApprovalEvent() {
    this._subscriptions.push(this._raciNavigationService._onSaveApproval.subscribe(data => {
      if (data) {
        this.updateActiveTask.emit(this.activeTask);
      }
    }));
  }

  /**
   * Select all properies
   */
  selectAllProperties() {
    this.isProjectSelected = !this.isProjectSelected;
    if (this.isProjectSelected) {
      this.selectedApprovals = this.properties;
    } else {
      this.selectedApprovals = [];
    }
    const displayValoremIcon = this.isProjectSelected && this.activeTask.taskActionStage === ActionStageEnum.Waiting;
    this._raciNavigationService.onSyncWithValorem(displayValoremIcon);
  }
}
